import React from 'react';
import PropTypes from 'prop-types';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import List from 'react-virtualized/dist/commonjs/List';
import CellMeasurer from 'react-virtualized/dist/commonjs/CellMeasurer';
import CellMeasurerCache from 'react-virtualized/dist/commonjs/CellMeasurer/CellMeasurerCache';
import isEqual from 'lodash/isEqual';

import SelectBox from './SelectBox';

class SelectList extends React.Component {
  constructor(props) {
    super(props);

    this.cellMesureCache = new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 35
    });

    this.menuHeight = props.maxMenuHeight;
    this.rowRenderer = this.rowRenderer.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.items, nextProps.items)) {
      this.cellMesureCache.clearAll();
    }
  }

  componentDidMount() {
    this.maybeUpdateMenuHeight();
  }

  componentDidUpdate() {
    this.maybeUpdateMenuHeight();
  }

  componentWillUnmount() {
    const { onUpdateMenuHeight, maxMenuHeight } = this.props;
    onUpdateMenuHeight(maxMenuHeight);
  }

  maybeUpdateMenuHeight() {
    const { items, maxMenuHeight, onUpdateMenuHeight } = this.props;
    const height = items.reduce((preHeight, _item, index) => {
      return preHeight + this.cellMesureCache.rowHeight({ index });
    }, 0);
    const menuHeight = height > maxMenuHeight ? maxMenuHeight : height;

    if (menuHeight !== this.menuHeight) {
      this.menuHeight = menuHeight;
      onUpdateMenuHeight(menuHeight);
    }
  }

  rowRenderer({ index, key, parent, style }) {
    const { renderItem, items } = this.props;
    return (
      <CellMeasurer
        cache={this.cellMesureCache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {renderItem({ item: items[index], index, style })}
      </CellMeasurer>
    );
  }

  render() {
    const { width, height, overscanCount, items, highlightedIndex } = this.props;
    return (
      <List
        // this is needed to make item clickable
        highlightedIndex={highlightedIndex}
        rowRenderer={this.rowRenderer}
        overscanRowCount={overscanCount}
        deferredMeasurementCache={this.cellMesureCache}
        width={width}
        height={height}
        rowHeight={this.cellMesureCache.rowHeight}
        rowCount={items.length}
        items={items} // To trigger re-render once items changed
      />
    );
  }
}

SelectList.propTypes = {
  items: PropTypes.array,
  renderItem: PropTypes.func.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  highlightedIndex: PropTypes.number,
  maxMenuHeight: PropTypes.number.isRequired,
  overscanCount: PropTypes.number.isRequired,
  onUpdateMenuHeight: PropTypes.func.isRequired
};

SelectList.defaultProps = {
  items: []
};

/**
 * VirtualizedSelectBox, extends [SelectBox](selectbox) to add virtualized support
 */
export default class VirtualizedSelectBox extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      menuHeight: props.maxMenuHeight
    };

    this.menuRenderer = this.menuRenderer.bind(this);
    this.handleUpdateMenuHeight = this.handleUpdateMenuHeight.bind(this);
  }

  handleUpdateMenuHeight(menuHeight) {
    this.setState({
      menuHeight
    });
  }

  renderList({ items, highlightedIndex, renderItem, width, height }) {
    return (
      <SelectList
        items={items}
        highlightedIndex={highlightedIndex}
        renderItem={renderItem}
        width={width}
        height={height}
        overscanCount={this.props.overscanCount}
        maxMenuHeight={this.props.maxMenuHeight}
        onUpdateMenuHeight={this.handleUpdateMenuHeight}
      />
    );
  }

  menuRenderer({ items, highlightedIndex, renderItem }) {
    return (
      <div style={{ height: this.state.menuHeight }}>
        <AutoSizer>
          {({ width, height }) =>
            this.renderList({ items, highlightedIndex, renderItem, width, height })
          }
        </AutoSizer>
      </div>
    );
  }

  render() {
    const { children } = this.props;
    return children({
      ...this.props,
      menuRenderer: this.menuRenderer
    });
  }
}

VirtualizedSelectBox.defaultProps = {
  ...SelectBox.defaultProps,
  // eslint-disable-next-line react/display-name
  children: props => <SelectBox {...props} />,
  overscanCount: 5
};

VirtualizedSelectBox.propTypes = {
  ...SelectBox.propTypes,
  /**
   * render function
   */
  children: PropTypes.func.isRequired,
  /**
   * overscan count
   */
  overscanCount: PropTypes.number
};
